import { usePlayerActions } from "@utils/player/core/usePlayer";
import List from "@components/Collection/List";
import AlbumGate from "@components/Collection/album/AlbumGate";
import { ViewArtistModalSWMSheet } from "@components/modals/ViewArtistModalSWM";
import type { Album as CatalogAlbum } from "@libs/wire";
import { useSongsByAlbumIdsSuspense } from "@hooks/trpc/useSongs";
import { handleArtistNavigation } from "@utils/index";
import { useCallback } from "react";
import dayjs from "dayjs";
import { useArtists } from "@hooks/trpc/useArtists";
import { Platform } from "react-native";

import { useBottomSheetManager } from "react-native-bottom-sheet-stack";
import { useSharedSongLink } from "@hooks/useSharedSongLink";

interface Props {
  id: string;
  songId?: string;
}

interface AlbumListProps extends Props {
  album: CatalogAlbum | undefined;
  isFetching: boolean;
}

const AlbumList = ({ id, songId, album, isFetching }: AlbumListProps) => {
  const playerActions = usePlayerActions();

  const {
    query: { data: songs },
  } = useSongsByAlbumIdsSuspense([id]);

  const numberOfArtists = album?.artistIds?.length || 0;
  const isOwner = false;

  const { query: artistsQuery } = useArtists(album?.artistIds || [], {
    enabled: !!album?.artistIds,
  });
  const artists = artistsQuery.data;
  const playFromCollection = playerActions.playFromCollection;
  const bottomSheetManager = useBottomSheetManager();

  const playSongOnLink = useCallback(
    (sharedSongId: string) => {
      playFromCollection({
        songId: sharedSongId,
        collectionType: "album",
        collectionId: id,
        collectionName: album?.title,
        songs,
        options: Platform.OS === "web" ? { autoPlay: false } : undefined,
      });
    },
    [album?.title, id, playFromCollection, songs],
  );

  useSharedSongLink({ onPlay: playSongOnLink, songId, songs });

  const handleSelectSong = useCallback(
    (songId: string) => {
      playFromCollection({
        songId,
        collectionType: "album",
        collectionId: id,
        collectionName: album?.title,
        songs,
      });
    },
    [album?.title, id, playFromCollection, songs],
  );

  const handlePress = () => {
    if (numberOfArtists === 1) {
      handleArtistNavigation({
        params: {
          isPlaylist: false,
          numberOfArtists,
          artistId: album?.artistIds?.at(0),
        },
      });
    } else {
      if (!artists || artists.length === 0) {
        return;
      }

      bottomSheetManager.open(
        <ViewArtistModalSWMSheet
          artists={artists.map((artist) => ({
            id: artist.id,
            name: artist.name,
            image: artist.image || "",
          }))}
        />,
        {
          mode: "push",
        },
      );
    }
  };

  // Unreleased exclusives have no release date; dayjs(null) is invalid and
  // would render "Artist • NaN".
  const releaseDate = album?.releaseDate ? dayjs(album.releaseDate) : null;
  const releaseYear = releaseDate?.isValid() ? releaseDate.year() : null;

  const hasPrivateSong = album?.hasPrivateSong;

  return (
    <List
      id={id}
      image={album?.image}
      isFetching={isFetching}
      isOwner={isOwner}
      isPublic={true}
      onPress={handlePress}
      onSelectSong={handleSelectSong}
      songs={songs || []}
      subtitle={
        releaseYear
          ? `${album?.artistDisplayText} • ${releaseYear}`
          : (album?.artistDisplayText ?? "")
      }
      subtitleNumberOfLines={2}
      title={album?.title}
      type="album"
      hasPrivateSong={hasPrivateSong}
    />
  );
};

const Album = ({ id, songId }: Props) => (
  <AlbumGate id={id}>
    {(album, isFetching) => (
      <AlbumList
        album={album}
        id={id}
        isFetching={isFetching}
        songId={songId}
      />
    )}
  </AlbumGate>
);

export default Album;
